"use client";
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import "../../scss/_investment-types.scss";
import stock from './../../../public/investment-type/stock.png';
import royalty from './../../../public/investment-type/royalty.png';
import placement from './../../../public/investment-type/placement.png';
import mutulfund from './../../../public/investment-type/mutulfund.png';
import commodity from './../../../public/investment-type/commodity.png';
import bond from './../../../public/investment-type/bond.png';
import stockgraphs from './../../../public/investment-type/stockgraphs.png';
import { getArticleDetailBySlug } from '@/services/publicService';
// Generated images from brainstorming sessions
const heroImg = "/hero_oil_platform_1773314620229.png";
const refineryImg = "/oil_refinery_tower_1773314636789.png";
const pumpjackImg = "/oil_pumpjack_1773314654955.png";
const signingImg = "/signing_financial_papers_1773314672363.png";
const chartImg = "/stock_market_chart_neon_1773314824297.png";
const handshakeImg = "/business_handshake_professional_1773314847717.png";
const sunsetImg = "/icons/hero.png"; // Fallback for the one that failed


export default function InvestmentTypes() {
  const [activeTab, setActiveTab] = useState("Beginner Path");
  const [openFaq, setOpenFaq] = useState(0);
  const [beginnerArticles, setBeginnerArticles] = useState([]);
  const [advancedArticles, setAdvancedArticles] = useState([]);
  const [featureArticles, setFeatureArticles] = useState([]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const investmentVehicles = [
    {
      icon: stock,
      title: "Oil & Gas Stocks",
      description: "Buy shares in public energy companies involved in exploration, production, or refining. A direct way to benefit from company performance."
    },
    {
      icon: placement,
      title: "Private Placements",
      description: "Learn about potentially high-return investments through partnerships in pipeline infrastructure."
    },
    {
      icon: mutulfund,
      title: "ETFs & Mutual Funds",
      description: "Diversify with funds that follow energy indexes, oil futures, or broader commodity markets. Great for beginners seeking lower risk."
    },
    {
      icon: commodity,
      title: "Commodity Funds & Futures",
      description: "Understand financial tools that track real-time oil prices to aid your decision-making process."
    },
    {
      icon: bond,
      title: "Bonds",
      description: "Explore fixed-income securities issued by energy companies or state-owned entities. Lower risk than stocks but with steady returns."
    },
    {
      icon: royalty,
      title: "Royalty Trusts",
      description: "Get returns from oil-producing assets without direct managerial responsibilities."
    }
  ];

  const beginnerArticlesData = [
    {
      title: "What Are Oil and Gas Stocks? A Beginner’s Guide",
      slug: "what-are-oil-and-gas-stocks-a-beginners-guide-693a6c67bd85a",
      description: "Learn how to invest in energy without picking individual stocks. ETFs offer diversification and lower entry costs for new investors.",
      image: refineryImg
    },
    {
      title: "Oil ETF Stock: What It Is, Best Oil ETFs of 2026 & Complete Guide",
      slug: "oil-etf-stock-what-it-is-best-oil-etfs-of-2026-complete-guide-69fde572551d2",
      description: "Discover how to choose, evaluate, and buy oil & gas company shares. Understand key metrics and what to look for in energy companies.",
      image: pumpjackImg
    },
    {
      title: "How Oil and Gas Investments Generate Passive Income",
      slug: "how-oil-and-gas-investments-generate-passive-income-6939838f3e37f",
      description: "A comprehensive guide covering entry points, common mistakes to avoid, and strategies for getting started in oil and gas investing.",
      image: signingImg
    }
  ];

  const advancedArticlesData = [
    {
      title: "Oil and Gas Limited Partnership Investments: An Overview",
      slug: "oil-and-gas-limited-partnership-investments-an-overview-697338162afb5",
      description: "Explore sophisticated investment strategies for experienced investors looking to maximize returns in volatile energy markets.",
      image: chartImg
    },
    {
      title: "Tax Advantages of Oil and Gas Investments: What to Know",
      slug: "tax-advantages-of-oil-and-gas-investments-what-to-know-69398496955a7",
      description: "Learn about private equity investments, their structure, and how to evaluate opportunities in the energy sector.",
      image: handshakeImg
    },
    {
      title: "Top 5 Midstream Oil and Gas Stocks to Watch & 5 High-Yield Picks for 2026",
      slug: "top-5-midstream-oil-and-gas-stocks-to-watch-5-high-yield-picks-for-2026-69fb138c55bbd",
      description: "Understand how to use futures contracts and derivatives to hedge positions or speculate on oil and gas price movements.",
      image: sunsetImg
    }
  ];

  const featureArticlesData = [
    {
      title: "Key Factors Influencing Oil Prices and Their Economic Impact",
      slug: "key-factors-influencing-oil-prices-and-their-economic-impact-69ccc59342be0",
      description: "Explore the best-performing ETFs tracking oil, gas, and renewables.",
      image: chartImg
    },
    {
      title: "Understanding Oil Futures: A Step-by-Step Guide to Trading",
      slug: "understanding-oil-futures-a-step-by-step-guide-to-trading-69f0fa3d6e74b",
      description: "A closer look at MLP structures and their income potential.",
      image: handshakeImg
    },
    {
      title: "The Role of Crude Oil in Today’s Markets: Why Investors Still Care",
      slug: "the-role-of-crude-oil-in-todays-markets-why-investors-still-care-69bea8599f21f",
      description: "How futures contracts work, who trades them, and how they impact global oil pricing.",
      image: sunsetImg
    }
  ];

  useEffect(() => {
    let cancelled = false;
    setBeginnerArticles(beginnerArticlesData);
    setAdvancedArticles(advancedArticlesData);
    setFeatureArticles(featureArticlesData);

    const fetchArticleImages = async (articles) => Promise.all(
      articles.map(async (article) => {
        const resp = await getArticleDetailBySlug(article.slug);
        const data = resp?.data || {};
        return {
          ...article,
          image: data.image?.url || data.image || data.featured_image || article.image,
        };
      })
    );

    (async () => {
      try {
        const [beginner, advanced, feature] = await Promise.all([
          fetchArticleImages(beginnerArticlesData),
          fetchArticleImages(advancedArticlesData),
          fetchArticleImages(featureArticlesData),
        ]);
        if (cancelled) return;
        setBeginnerArticles(beginner);
        setAdvancedArticles(advanced);
        setFeatureArticles(feature);
      } catch (error) {
        if (!cancelled) {
          setBeginnerArticles(beginnerArticlesData);
          setAdvancedArticles(advancedArticlesData);
          setFeatureArticles(featureArticlesData);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const faqs = [
    {
      question: "What is the safest way to invest in oil and gas?",
      answer: "The safest way depends on your risk tolerance and investment timeline. For beginners, ETFs and mutual funds offer diversification and lower risk. For more experienced investors, royalty trusts can provide steady income with less direct exposure to operational risks."
    },
    {
      question: "Are MLPs good for long-term investment?",
      answer: "MLPs (Master Limited Partnerships) can offer high-yield returns and tax advantages, making them attractive for long-term income-focused investors. However, they're best suited for experienced investors due to their tax obligations and potential liquidity concerns."
    },
    {
      question: "Can individuals invest in royalty interests?",
      answer: "Yes, individuals can invest in royalty interests. You can purchase shares in publicly traded royalty trusts or, in some cases, acquire mineral rights directly. This allows you to receive income from oil and gas production without managing operations."
    },
    {
      question: "What is the difference between Oil ETFs and mutual funds?",
      answer: "Oil ETFs (Exchange-Traded Funds) trade like stocks throughout the day and typically have lower expense ratios. Mutual funds are bought and sold at the end of the trading day at net asset value and are often actively managed. ETFs are more suitable for trading, while mutual funds may focus on broader energy investment strategies."
    },
    {
      question: "Can I invest in oil without owning physical barrels?",
      answer: "Absolutely. You can invest in oil without owning physical barrels through various financial instruments including stocks in oil companies, ETFs that track oil prices, MLPs, royalty trusts, and futures contracts. These provide exposure to oil prices without the need for physical storage."
    }
  ];

  return (
    <div className="investment-types-page-it">
      {/* Hero Section */}
      <section className="hero-section-it">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="hero-content-it">
                <span className="hero-label-it">INVESTMENT TYPES</span>
                <h1 className="hero-title-it">Explore Oil & Gas Investment Types</h1>
                <p className="hero-description-it">
                  Understand the full range of investment options available in the energy industry—from stocks and bonds to ETFs, royalties to equity, and more. Make the choice that fits your bill!
                </p>
                <div className="hero-buttons-it">
                  <button className="btn-primary-it" onClick={() => document.getElementById('investment-vehicles-section-it')?.scrollIntoView({ behavior: 'smooth' })}>Start Exploring</button>
                  <button className="btn-secondary-it" onClick={() => document.getElementById('investment-paths-section-it')?.scrollIntoView({ behavior: 'smooth' })}>What&apos;s Right For Me?</button>
                </div>
              </div>
              <div className="hero-image-wrapper-it">
                <Image
                  src={stockgraphs}
                  alt="Oil Tanker and Industrial Facility"
                  className="hero-image-it"
                  width={1200}
                  height={656}
                  priority
                  style={{ objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Investment Vehicle Section */}
      <section className="investment-vehicles-section-it" id="investment-vehicles-section-it">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="section-title-it">Choose Your Investment Vehicle</h2>
            </div>
          </div>
          <div className="row">
            {investmentVehicles.map((vehicle, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="investment-vehicle-card-it">
                  <div className="vehicle-icon-it">
                    <Image
                      src={vehicle.icon}
                      alt={vehicle.title}
                      width={48}
                      height={48}
                    />
                  </div>
                  <h3 className="vehicle-title-it">{vehicle.title}</h3>
                  <p className="vehicle-description-it">{vehicle.description}</p>
                  {/* <a href="#" className="vehicle-link-it">
                    Read More
                    <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_4761_2794)">
                        <path d="M15.695 16.83L20.5 12L15.695 7.16998C15.6516 7.11299 15.5965 7.066 15.5333 7.03217C15.4702 6.99834 15.4005 6.97848 15.329 6.97392C15.2576 6.96935 15.1859 6.98021 15.119 7.00573C15.0521 7.03126 14.9914 7.07087 14.9411 7.12188C14.8909 7.17289 14.8521 7.23411 14.8275 7.30139C14.803 7.36867 14.7931 7.44045 14.7987 7.51186C14.8043 7.58327 14.8252 7.65265 14.8599 7.7153C14.8946 7.77795 14.9424 7.8324 15 7.87498L18.595 11.5L9.03 11.5C8.89739 11.5 8.77022 11.5527 8.67645 11.6464C8.58268 11.7402 8.53 11.8674 8.53 12C8.53 12.1326 8.58268 12.2598 8.67645 12.3535C8.77022 12.4473 8.89739 12.5 9.03 12.5L18.595 12.5L15 16.125C14.9065 16.2191 14.8543 16.3466 14.8547 16.4792C14.8552 16.6119 14.9083 16.739 15.0025 16.8325C15.0967 16.926 15.2241 16.9782 15.3568 16.9778C15.4894 16.9773 15.6165 16.9241 15.71 16.83H15.695Z" fill="#0C0C0C" />
                      </g>
                      <rect x="26" y="2.5" width="19" height="23" rx="9.5" transform="rotate(90 26 2.5)" stroke="#0C0C0C" />
                      <defs>
                        <clipPath id="clip0_4761_2794">
                          <rect x="25.5" y="3" width="18" height="22" rx="9" transform="rotate(90 25.5 3)" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>

                  </a> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beginner vs. Advanced Investment Paths Section */}
      <section className="investment-paths-section-it" id="investment-paths-section-it">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className='wrapper-investment'>
                <h2 className="section-title-it">Beginner vs. Advanced Investment Paths</h2>
                <p className="section-description-it">
                  Choose a learning path that matches your experience whether you are starting out or are ready to take on more complex investments in the energy industry.
                </p>
              </div>
              
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="tabs-container-it">
                <button
                  className={`tab-button-it ${activeTab === "Beginner Path" ? "active-it" : ""}`}
                  onClick={() => setActiveTab("Beginner Path")}
                >
                  Beginner Path
                </button>
                <button
                  className={`tab-button-it ${activeTab === "Advanced Path" ? "active-it" : ""}`}
                  onClick={() => setActiveTab("Advanced Path")}
                >
                  Advanced Path
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="tab-content-it">
                {(activeTab === "Beginner Path" ? beginnerArticles : advancedArticles).map((article, index) => (
                  <div key={index} className="article-card-it">
                    <div className="article-content-it">
                      <h3 className="article-title-it">{article.title}</h3>
                      <p className="article-description-it">{article.description}</p>
                      <Link href={article?.slug ? `/article/${article.slug}` : '#'} className="article-link-it">
                        Read More
                        <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_4761_1117)">
                        <path d="M15.695 16.83L20.5 12L15.695 7.16998C15.6516 7.11299 15.5965 7.066 15.5333 7.03217C15.4702 6.99834 15.4005 6.97848 15.329 6.97392C15.2576 6.96935 15.1859 6.98021 15.119 7.00573C15.0521 7.03126 14.9914 7.07087 14.9411 7.12188C14.8909 7.17289 14.8521 7.23411 14.8275 7.30139C14.803 7.36867 14.7931 7.44045 14.7987 7.51186C14.8043 7.58327 14.8252 7.65265 14.8599 7.7153C14.8946 7.77795 14.9424 7.8324 15 7.87498L18.595 11.5L9.03 11.5C8.89739 11.5 8.77022 11.5527 8.67645 11.6464C8.58268 11.7402 8.53 11.8674 8.53 12C8.53 12.1326 8.58268 12.2598 8.67645 12.3535C8.77022 12.4473 8.89739 12.5 9.03 12.5L18.595 12.5L15 16.125C14.9065 16.2191 14.8543 16.3466 14.8547 16.4792C14.8552 16.6119 14.9083 16.739 15.0025 16.8325C15.0967 16.926 15.2241 16.9782 15.3568 16.9778C15.4894 16.9773 15.6165 16.9241 15.71 16.83H15.695Z" fill="#0C0C0C"/>
                        </g>
                        <rect x="26" y="2.5" width="19" height="23" rx="9.5" transform="rotate(90 26 2.5)" stroke="#0C0C0C"/>
                        <defs>
                        <clipPath id="clip0_4761_1117">
                        <rect x="25.5" y="3" width="18" height="22" rx="9" transform="rotate(90 25.5 3)" fill="white"/>
                        </clipPath>
                        </defs>
                        </svg>

                      </Link>
                    </div>
                    <div className="article-image-wrapper-it">
                      <Image
                        src={article.image}
                        alt={article.title}
                        fill
                        style={{ objectFit: 'cover' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-4">
                <Link href="/category/tax-and-finance" className="btn-view-more-it" style={{ textDecoration: 'none' }}>
                  View More Posts
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Articles Section */}
      <section className="feature-articles-section-it">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="section-title-it">Feature Articles On Financial Instruments and Energy Market</h2>
            </div>
          </div>
          <div className="row">
            {featureArticles.map((article, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="feature-article-card-it">
                  <div className="feature-article-image-wrapper-it">
                    <Image
                      src={article.image}
                      alt={article.title}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className="feature-article-content-it">
                    <h3 className="feature-article-title-it">{article.title}</h3>
                    <p className="feature-article-description-it">{article.description}</p>
                    <a href="#" className="feature-article-link-it">
                      Read More
                       <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_4761_2794)">
                        <path d="M15.695 16.83L20.5 12L15.695 7.16998C15.6516 7.11299 15.5965 7.066 15.5333 7.03217C15.4702 6.99834 15.4005 6.97848 15.329 6.97392C15.2576 6.96935 15.1859 6.98021 15.119 7.00573C15.0521 7.03126 14.9914 7.07087 14.9411 7.12188C14.8909 7.17289 14.8521 7.23411 14.8275 7.30139C14.803 7.36867 14.7931 7.44045 14.7987 7.51186C14.8043 7.58327 14.8252 7.65265 14.8599 7.7153C14.8946 7.77795 14.9424 7.8324 15 7.87498L18.595 11.5L9.03 11.5C8.89739 11.5 8.77022 11.5527 8.67645 11.6464C8.58268 11.7402 8.53 11.8674 8.53 12C8.53 12.1326 8.58268 12.2598 8.67645 12.3535C8.77022 12.4473 8.89739 12.5 9.03 12.5L18.595 12.5L15 16.125C14.9065 16.2191 14.8543 16.3466 14.8547 16.4792C14.8552 16.6119 14.9083 16.739 15.0025 16.8325C15.0967 16.926 15.2241 16.9782 15.3568 16.9778C15.4894 16.9773 15.6165 16.9241 15.71 16.83H15.695Z" fill="#0C0C0C" />
                      </g>
                      <rect x="26" y="2.5" width="19" height="23" rx="9.5" transform="rotate(90 26 2.5)" stroke="#0C0C0C" />
                      <defs>
                        <clipPath id="clip0_4761_2794">
                          <rect x="25.5" y="3" width="18" height="22" rx="9" transform="rotate(90 25.5 3)" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* New to Oil & Gas Investments Section */}
      <section className="cta-section-it">
        <div className="container">
          <div className="row">
            <div className="col-md-5 text-center">
              <h2 className="cta-title-it">New to Oil & Gas Investments?</h2>
            </div>
            <div className='col-md-7'>
              <p className="cta-description-it">
                Thinking of starting your journey? Our beginner&apos;s guide will help you choose the right investment type based on your financial goals and comfort with risk.
              </p>
              <button className="btn-cta-it">Download Our Beginner&apos;s Guide</button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section-it">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="faq-title-it">Frequently Asked Questions</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="faq-list-it">
                {faqs.map((faq, index) => (
                  <div key={index} className="faq-item-it">
                    <button
                      className={`faq-question-it ${openFaq === index ? 'active-it' : ''}`}
                      onClick={() => toggleFaq(index)}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-icon-it">
                        {openFaq === index ? '−' : '+'}
                      </span>
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer-it">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

